import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useField } from 'react-final-form';
import {
  Accordion,
  DateRangeField,
  ExplanationTooltip,
  InputField,
  SelectField,
  TimeField
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { required } from '@ubeya/shared/utils/form';
import { MAIN_PROJECT_COLORS, MAIN_PROJECT_NO_COLOR } from '@ubeya/shared/constants';
import useGroupedBranchesOptions from '../../shared/hooks/useGroupedBranchesOptions';

export const ALL_BRANCHES_VALUE = 'all';

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const NameRowGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const MoreContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
  width: 100%;
`;

const ColorCircle = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 100%;
  background: ${({ $color }) => $color || MAIN_PROJECT_NO_COLOR};
  border: ${({ $color }) => ($color ? 'none' : '1px solid #d0d0d0')};
`;

const COLORS_OPTIONS = MAIN_PROJECT_COLORS.map(({ color }) => ({ value: color, label: color }));

const MainProjectDetailsStep = ({ lockedBranchIds }) => {
  const { t } = useContext(ConfigContext);
  const {
    input: { value: selectedBranchIds }
  } = useField('selectedBranchIds', { subscription: { value: true } });
  const {
    branchesOptions,
    groupedBranchesOptions,
    getOptionSpecificStyle,
    filterFunc,
    expandBranchIds
  } = useGroupedBranchesOptions({ isGroupSelectable: true, selectedBranchIds });
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const branchesOptionsWithAll = useMemo(() => {
    const lockedBranchIdsSet = new Set(lockedBranchIds || []);
    return [
      { value: ALL_BRANCHES_VALUE, label: t('allBranches') },
      ...groupedBranchesOptions.map((option) =>
        lockedBranchIdsSet.has(option.value) ? { ...option, isChipDisabled: true } : option
      )
    ];
  }, [groupedBranchesOptions, lockedBranchIds, t]);

  const handleBranchesChange = useCallback(
    (value, input) => {
      const expandedValue = expandBranchIds(value);
      if (!lockedBranchIds) {
        input.onChange(expandedValue);
        return;
      }
      const selected = expandedValue.includes(ALL_BRANCHES_VALUE)
        ? branchesOptions.map((option) => option.value)
        : expandedValue;
      input.onChange([...new Set([...lockedBranchIds, ...selected])]);
    },
    [branchesOptions, expandBranchIds, lockedBranchIds]
  );

  return (
    <>
      <NameRowGrid>
        <InputField name="name" title={t('mainEventName')} validate={required} required />
        <DateRangeField
          name="dateRange"
          title={<ExplanationTooltip title={`${t('date')} *`} body={t('createMainEventDatesTooltip')} />}
          validate={required}
          clearable={false}
          withDay
          showTotalDays
          usePortal
        />
      </NameRowGrid>
      <TwoColumnGrid>
        <TimeField name="startTime" title={t('mainEventStartTime')} timeOnly clearable usePortal />
        <TimeField name="doorTime" title={t('mainEventDoorTime')} timeOnly clearable usePortal />
      </TwoColumnGrid>
      <TwoColumnGrid>
        <SelectField
          name="color"
          title={t('color')}
          options={COLORS_OPTIONS}
          isMulti={false}
          formatValue={({ value }) => <ColorCircle $color={value} />}
          formatOption={({ value }) => ({ value, label: <ColorCircle $color={value} /> })}
          usePortal
        />
        <SelectField
          name="selectedBranchIds"
          title={t('branches')}
          options={branchesOptionsWithAll}
          isMulti
          usePortal
          onChange={handleBranchesChange}
          getOptionSpecificStyle={getOptionSpecificStyle}
          filterFunc={filterFunc}
        />
      </TwoColumnGrid>
      <Accordion>
        <Accordion.Item title={t('moreDetails')} isOpen={isMoreOpen} onClick={() => setIsMoreOpen((prev) => !prev)}>
          <MoreContent>
            <TwoColumnGrid>
              <InputField name="identifier" title={t('mainEventIdentifier')} />
              <InputField name="glCode" title={t('mainEventGlCode')} />
              <InputField name="expectedRevenue" title={t('mainEventExpectedRevenue')} />
              <InputField name="totalActualRevenue" title={t('mainEventTotalActualRevenue')} />
              <InputField name="estimatedAttendance" title={t('mainEventEstimatedAttendance')} />
              <InputField name="actualAttendance" title={t('mainEventActualAttendance')} />
            </TwoColumnGrid>
          </MoreContent>
        </Accordion.Item>
      </Accordion>
    </>
  );
};

export default MainProjectDetailsStep;
